import { isBrowser } from '@mc/base';
import NottaLogo from '../images/Logo-Notta-Dark.png';
import { ICMSLink } from './type';
import {
  SUPPORT_PAGE_LANGUAGES,
  WEB_APP_DEV_HOST_URL,
  WEB_APP_EN_LOGIN_IN_URL,
  WEB_APP_EN_SIGN_UP_URL,
  WEB_APP_HOST_URL,
  WEB_APP_TEST_HOST_URL,
  WEB_APP_UAT_HOST_URL,
} from '@/constants';

export const BrandLogo = NottaLogo;
export const getPrefixCls = (type: string) => {
  return `notta-${type}`;
};
/**
 * 日文导航栏的高度
 */
export const NAV_HEIGHT_JP = 68;

type Options = {
  format?: string;
  quality?: number;
  blur: [number?, number?];
};

export const processCdnUrl = (url: string, options: Options = { format: 'webp', quality: 100, blur: [0, 10] }) => {
  const { format, quality, blur } = options;

  return `${url}/m/filters:format(${format}):quality(${quality}):blur(${blur[0]})`;
};

export const blogPathArr = [
  '/blog/mp4-to-text',
  '/blog/best-voice-recorder',
  '/blog/mac-transcription',
  '/blog/10-best-google-chrome-extensions',
  '/blog/youtube-transcription',
];

export const getHotBlogParams = () => {
  if (typeof window === 'undefined') return null;
  const isHotBlog = blogPathArr.includes(window.location.pathname);
  return isHotBlog ? window.location.pathname.replace('/blog/', '') : null;
};

export const getABTestLoginSignUpUrl = (type: 'login' | 'signup') => {
  if (typeof window === 'undefined') return null;
  const origin = window.location.origin;

  let currentOrigin = 'https://app.notta.ai';
  if (origin.includes('dev')) {
    currentOrigin = 'https://dev-c0ee1fac76df136a.notta.ai';
  } else if (origin.includes('test')) {
    currentOrigin = 'https://test-app-12154205867b06a3.notta.ai';
  }

  return getHotBlogParams()
    ? `${currentOrigin}/${type}?language=ja&from=official&blog=${getHotBlogParams()}`
    : `${currentOrigin}/${type}?language=ja&from=official`;
};

// 加载脚本
export const loadScript = (src: string, id: string, className?: string) => {
  return new Promise<void>((resolve, reject) => {
    const isExitScript = document.getElementById(id);
    if (isExitScript) return;

    const script = document.createElement('script');
    script.id = id;
    script.classList.add(className);
    script.defer = true;
    script.src = src;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      reject();
    };
    document.body.appendChild(script);
  });
};

export const addLeadingZero = (number) => {
  return number > 0 && number < 10 ? `0${number}` : `${number}`;
};

export const randomIntFromInterval = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const getNumberFromRange = (min: number, max: number, n: number) => {
  if (n > max) {
    return max;
  } else if (n < min) {
    return min;
  } else {
    return n;
  }
};

export const loadFont = (fontFamily, fontStyle, fontWeight, url) => {
  const font = new FontFace(fontFamily, `url(${url})`, {
    style: fontStyle,
    weight: fontWeight,
  });

  font
    .load()
    .then((loadedFont) => {
      if (isBrowser()) {
        document.fonts?.add(loadedFont);
      }
    })
    .catch(() => {});
};

export type SIGN_TYPE = 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export interface IResizeSchema {
  base?: any;
  sm?: any;
  md?: any;
  lg?: any;
  xl?: any;
  '2xl'?: any;
}

export const WINDOW_SIZE = {
  base: 575,
  sm: 767,
  md: 991,
  lg: 1200,
  xl: 1600,
};

export const calcWindowSizeSign = (windowWidth): SIGN_TYPE => {
  if (windowWidth <= WINDOW_SIZE.base) {
    return 'base';
  } else if (windowWidth <= WINDOW_SIZE.sm) {
    return 'sm';
  } else if (windowWidth <= WINDOW_SIZE.md) {
    return 'md';
  } else if (windowWidth <= WINDOW_SIZE.lg) {
    return 'lg';
  } else if (windowWidth <= WINDOW_SIZE.xl) {
    return 'xl';
  } else {
    return '2xl';
  }
};

export const getValueBySignSchema = (sign: SIGN_TYPE, schema: IResizeSchema) => {
  const SIGN_ARR: SIGN_TYPE[] = ['base', 'sm', 'md', 'lg', 'xl', '2xl'];

  const value = schema[sign];

  if (value) {
    return value;
  }

  const startIndex = SIGN_ARR.indexOf(sign) - 1;

  for (let i = startIndex; i >= 0; i--) {
    const tempSign = SIGN_ARR[i];
    if (schema[tempSign]) {
      return schema[tempSign];
    }
  }

  return '';
};

/**
 * 获取最大距离没有重复元素的数组
 * @param arr 资源数组
 * @param n 需要取多少个元素
 * @returns 最大距离没有重复元素的数组
 */
export const largestDistanceNoRepeatArr = (arr: Array<any>, n: number): Array<any> => {
  const arrLen = arr.length;
  if (arrLen >= n) {
    return arr;
  }
  const repeatCount = Math.floor(n / arrLen);
  const remainCount = n % arrLen;
  let leftPos = 0,
    rightPos = arrLen - 1;
  let isLeftPosLastMove = false;
  while (rightPos - leftPos + 1 > remainCount) {
    if (!isLeftPosLastMove) {
      leftPos++;
      isLeftPosLastMove = true;
    } else {
      rightPos--;
      isLeftPosLastMove = false;
    }
  }
  const suppleArr = arr.slice(leftPos, rightPos + 1);
  const res = [];
  for (let i = 0; i < repeatCount; ++i) {
    res.push(...arr);
  }
  res.push(...suppleArr);
  return res;
};

/**
 * 替换<wbr>和<br>标签为空字符串
 * @param inputString 待处理的字符串
 * @returns 把<wbr/>和<br/>替换成空格后的字符串
 */
export const removeWbrAndBr = (inputString) => {
  if (typeof inputString !== 'string') return '';
  // 使用正则表达式替换<wbr>和<br>标签为空字符串
  const outputString = inputString.replace(/<wbr\s*\/?>/gi, '').replace(/<br\s*\/?>/gi, '');

  return outputString;
};

/**
 * 根据CMS link获取URL
 * @param link CMS link 类型
 * @returns 能直接使用的url
 */
export const getRealLinkByCMSLink = (link: ICMSLink) => {
  if (link.linktype === 'url') {
    return link.url;
  }
  return `/${link.cached_url}`;
};

export const getPriceSuffix = (type) => {
  if (type === 'en') {
    return 'USD';
  }
  return '円';
};

declare global {
  interface Window {
    dataLayer: any;
  }
}
type GA4Event = Record<string, any> & { event?: string };
/**
 * 业务数据分析。
 * @param eventParams 事件参数
 */
export const GaDataLayer = (eventParams: GA4Event): void => {
  window.dataLayer.push(eventParams);
};

/**
 * 获取用户浏览器语言
 * @returns
 */
export const getUserBrowserLanguage = () => {
  if (typeof navigator === 'undefined') {
    return 'en';
  }
  const browserLanguage = navigator.language === 'ja' ? 'ja' : 'en';
  return browserLanguage;
};

/**
 * 判断某个dom节点是否需要显示滚动条
 * @param element dom节点
 * @returns 改dom节点是否需要滚动条
 */
export const isScrollbarNeeded = (element: Element) => {
  // 获取节点的实际高度和显示高度
  const scrollHeight = element.scrollHeight;
  const clientHeight = element.clientHeight;

  // 判断是否需要滚动条
  const scrollbarNeeded = scrollHeight > clientHeight;

  return scrollbarNeeded;
};

export enum ElementPosition {
  TOP,
  IN_VIEW,
  BOTTOM,
}

/**
 * 判断某个元素是否在滚动元素的可视区域内
 * @param el 需要判断的元素
 * @param container 滚动容器
 * @returns 改元素是否在滚动元素的可视区域内
 */
export const elementInContainerPosition = (el: HTMLElement, container: HTMLElement): ElementPosition => {
  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  let pos: ElementPosition = ElementPosition.IN_VIEW;

  if (elRect.top < containerRect.top) {
    pos = ElementPosition.TOP;
  } else if (elRect.bottom > containerRect.bottom) {
    pos = ElementPosition.BOTTOM;
  }

  return pos;
};

/**
 * sleep
 */
export const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * 将字符串转换为正则表达式
 *  */
export const createSearchRegex = (searchString) => {
  // 使用RegExp构造函数创建正则表达式，对特殊字符进行转义
  const escapedString = searchString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(escapedString, 'i'); // 'i' 表示不区分大小写
};

/**
 * 从CMS的full slug获取国际化语言
 * @param fullSlug
 */
export const getLangFromCmsFullSlug = (fullSlug: string) => {
  const lng = fullSlug.split('/')[0];
  return SUPPORT_PAGE_LANGUAGES.includes(lng) ? lng : 'ja';
};

/**
 * 根据语言的标识获取该语言在多语言表中的key
 */
export const getLanguageKey = (lang: string) => {
  return `language_${lang}`;
};

/**
 * 根据环境变量获取host
 * @returns
 */
export const getHostUrlByEnv = () => {
  if (process.env.GATSBY_PROJECT_ENV === 'dev') {
    return WEB_APP_DEV_HOST_URL;
  } else if (process.env.GATSBY_PROJECT_ENV === 'test') {
    return WEB_APP_TEST_HOST_URL;
  } else if (process.env.GATSBY_PROJECT_ENV === 'uat') {
    return WEB_APP_UAT_HOST_URL;
  } else {
    return WEB_APP_HOST_URL;
  }
};

export const dealWithJaSegment = (inputValue: string) => {
  if (inputValue.includes('<wbr')) {
    return inputValue;
  }

  if (typeof Intl['Segmenter'] === 'function') {
    const segmenter = new Intl['Segmenter']('ja-JP', { granularity: 'word' });

    let resStr = '';
    const segments = [];
    const arr = Array.from(segmenter.segment(inputValue));
    for (const segment of arr) {
      segments.push(segment['segment']);
      resStr += segment['segment'] + '<wbr />';
    }
    return resStr;
  }

  return inputValue;
};
export const emitEvent = (eventName, detail = {}) => {
  const event = new CustomEvent(eventName, { detail });
  window.dispatchEvent(event);
};

export const getWebAppSignUpUrlByLang = (lang: string) => {
  return WEB_APP_EN_SIGN_UP_URL.replace('language=en', `language=${lang}`);
};

export const getWebAppLoginUrlByLang = (lang: string) => {
  return WEB_APP_EN_LOGIN_IN_URL.replace('language=en', `language=${lang}`);
};

export const numericPricingPageSeats = (seats) => {
  return isNaN(Number(seats)) ? 0 : Number(seats);
};

/**
 * 格式化数字
 * @param input
 * @param decimalPlaces
 * @returns
 */
export const formatNumber = (input, decimalPlaces = 1) => {
  // 尝试将输入转换为数字
  const number = Number(input);

  // 检查转换后的数字是否是有效的数字
  if (Number.isNaN(number)) {
    throw new Error('Invalid input: input is not a number.');
  }

  // 根据是否为整数来格式化输出
  if (Number.isInteger(number)) {
    return number.toFixed(decimalPlaces);
  } else {
    // 如果已经是小数，根据指定的小数位数进行格式化
    return number.toFixed(decimalPlaces);
  }
};

/**
 * 计算并返回星星的评分列表
 * @param rate
 * @param totalRate
 * @returns
 */
export const calculateStarRatingsList = (rate, totalRate = 5) => {
  const starsRateList = Array.from({ length: totalRate }, (_, index) => {
    const count = index + 1;
    if (rate >= count) {
      return 1;
    } else if (rate < count && rate > count - 1) {
      return rate - (count - 1);
    } else {
      return 0;
    }
  });
  return starsRateList;
};
